import * as React from "react";
import { CircleAlertIcon, InboxIcon } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@crewfsm/ui/components/card";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@crewfsm/ui/components/empty";
import { cn } from "@crewfsm/ui/lib/utils";

function PageHeader({
  actions,
  className,
  description,
  eyebrow,
  title,
  ...props
}: Omit<React.ComponentProps<"header">, "title"> & {
  actions?: React.ReactNode;
  description?: React.ReactNode;
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
}) {
  return (
    <header
      className={cn(
        "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between",
        className,
      )}
      data-slot="page-header"
      {...props}
    >
      <div className="min-w-0">
        {eyebrow ? (
          <div className="mb-1 text-xs font-semibold tracking-[0.12em] text-muted-foreground uppercase">
            {eyebrow}
          </div>
        ) : null}
        <h1 className="text-2xl font-bold tracking-[-0.035em] text-foreground">
          {title}
        </h1>
        {description ? (
          <div className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {description}
          </div>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {actions}
        </div>
      ) : null}
    </header>
  );
}

function FilterBar({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-lg border border-border bg-card p-3 sm:flex-row sm:flex-wrap sm:items-center",
        className,
      )}
      data-slot="filter-bar"
      {...props}
    />
  );
}

function StatCard({
  className,
  description,
  icon,
  label,
  value,
  ...props
}: React.ComponentProps<typeof Card> & {
  description?: React.ReactNode;
  icon?: React.ReactNode;
  label: React.ReactNode;
  value: React.ReactNode;
}) {
  return (
    <Card className={cn("p-4", className)} data-slot="stat-card" {...props}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <div className="mt-2 text-2xl font-bold tracking-[-0.035em] text-foreground">
            {value}
          </div>
        </div>
        {icon ? (
          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-secondary text-secondary-foreground">
            {icon}
          </div>
        ) : null}
      </div>
      {description ? (
        <div className="mt-2 text-xs text-muted-foreground">{description}</div>
      ) : null}
    </Card>
  );
}

function DetailSection({
  children,
  className,
  description,
  title,
  ...props
}: Omit<React.ComponentProps<"section">, "title"> & {
  description?: React.ReactNode;
  title: React.ReactNode;
}) {
  return (
    <section
      className={cn("rounded-lg border border-border bg-card", className)}
      data-slot="detail-section"
      {...props}
    >
      <div className="border-b border-border px-4 py-3">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        {description ? (
          <div className="mt-0.5 text-sm text-muted-foreground">
            {description}
          </div>
        ) : null}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function FormSection(props: React.ComponentProps<typeof DetailSection>) {
  return <DetailSection data-slot="form-section" {...props} />;
}

function ChartWrapper({
  children,
  className,
  description,
  title,
  ...props
}: Omit<React.ComponentProps<typeof Card>, "title"> & {
  description?: React.ReactNode;
  title: React.ReactNode;
}) {
  return (
    <Card
      className={cn("min-w-0", className)}
      data-slot="chart-wrapper"
      {...props}
    >
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function EmptyState({
  action,
  className,
  description,
  icon,
  title,
  ...props
}: Omit<React.ComponentProps<typeof Empty>, "title"> & {
  action?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  title: React.ReactNode;
}) {
  return (
    <Empty className={cn("border border-border", className)} {...props}>
      <EmptyHeader>
        <EmptyMedia variant="icon">{icon ?? <InboxIcon />}</EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        {description ? (
          <EmptyDescription>{description}</EmptyDescription>
        ) : null}
      </EmptyHeader>
      {action ? <EmptyContent>{action}</EmptyContent> : null}
    </Empty>
  );
}

function ErrorState({
  action,
  className,
  description,
  title = "Something went wrong",
  ...props
}: Omit<React.ComponentProps<typeof Empty>, "title"> & {
  action?: React.ReactNode;
  description?: React.ReactNode;
  title?: React.ReactNode;
}) {
  return (
    <Empty
      className={cn("border border-danger/25 bg-danger-soft", className)}
      role="alert"
      {...props}
    >
      <EmptyHeader>
        <EmptyMedia className="bg-danger-soft text-danger" variant="icon">
          <CircleAlertIcon />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        {description ? (
          <EmptyDescription>{description}</EmptyDescription>
        ) : null}
      </EmptyHeader>
      {action ? <EmptyContent>{action}</EmptyContent> : null}
    </Empty>
  );
}

export {
  ChartWrapper,
  DetailSection,
  EmptyState,
  ErrorState,
  FilterBar,
  FormSection,
  PageHeader,
  StatCard,
};
